/**
 * Off-screen chute accumulation model (PLAYTEST F3 part 2, LOCKED-DESIGN 18.19).
 *
 * The S08 frustum sweep found **0 of 3 chutes on screen** from spawn, mid-arena,
 * the chamber approach and the north-east flank — the player is blind to every
 * intake for the whole core loop and only learns about a backlog when the BINS
 * meter climbs. This module is the missing feedback channel, expressed as a pure
 * function so the rules stay testable without a DOM or a camera.
 *
 * COMPOSITION (do not collide): the in-world mini-batch telegraph
 * (`arena.setChuteTelegraph`) is the ON-SCREEN channel — a mouth flash on the
 * chute itself. This model is the OFF-SCREEN channel. A chute whose outlet
 * projects inside the safe band is reported `hidden` precisely because the
 * in-world flash already owns it; the two never fire for the same chute at the
 * same time.
 *
 * It changes no pressure tuning: bulk is read, never written, and the spawn
 * schedule is untouched (F3 part 1 — the drop-rate question — is deliberately
 * out of scope until this ships).
 */

/** Live per-chute reading assembled by the Engine each frame. */
export interface ChuteSignal {
  id: "left" | "center" | "right";
  /** container-space px of the chute outlet, already projected */
  screenX: number;
  screenY: number;
  /** bulk of live, un-held pieces physically piled at the outlet */
  pileBulk: number;
  /**
   * bulk scheduled to drop from this chute inside the telegraph horizon. Zero
   * while critical overflow has shuttered the chutes (`upcomingSpawns` returns
   * `[]`), so the cue then reports the physical pile alone and never claims
   * incoming trash that cannot arrive.
   */
  incomingBulk: number;
}

export interface ChuteIndicatorSpec {
  id: string;
  /** `edge` = draw a directional chevron; `hidden` = no chevron this frame */
  kind: "edge" | "hidden";
  x: number;
  y: number;
  /** 0..7 compass octant from atan2, indexing the HUD's arrow glyph table */
  octant: number;
  level: 0 | 1 | 2 | 3;
  label: string;
  pulse: boolean;
  /**
   * `pileBulk + incomingBulk`. Carried alongside the label so the BINS-meter
   * pips can show the adult numeral without re-parsing the (mode-dependent)
   * label string.
   */
  bulk: number;
}

/**
 * Centre-band inset used by BOTH the edge-flare placement and the show test.
 * A projection outside this inset counts as off-screen.
 */
export const EDGE_BAND_MARGIN_PX = 46;
/**
 * Hysteresis partner of `EDGE_BAND_MARGIN_PX`: an indicator that is already up
 * only retracts once its chute projects this far inside the frame. A chute
 * sitting exactly on the margin therefore cannot flicker on/off frame by frame.
 */
export const EDGE_BAND_HIDE_MARGIN_PX = 56;

/** Minimum centre-to-centre gap between two chute chevrons. */
export const MIN_SEPARATION_PX = 48;
/**
 * Horizontal clearance under which two chevrons are treated as sharing a row.
 * Comfortably wider than the widest indicator pill (the adult "WEST · 12"
 * variant and the 1.4x Simple variant both sit well under this), so separating
 * a same-row pair vertically by `MIN_SEPARATION_PX` cannot leave their rects
 * overlapping — which `hudReadabilityIssues` flags between EVERY pair of
 * `data-hud-element`s.
 */
export const ROW_CLEARANCE_PX = 200;

const SEPARATION_PASSES = 12;

/** Compass word per chute, from the authored world positions (18.13 layout). */
const CHUTE_DIRECTION: Record<ChuteSignal["id"], string> = {
  left: "WEST",
  center: "SOUTH",
  right: "EAST",
};

export interface EdgeBandPlacement {
  /** true when the point is inside the margin box (no edge cue needed) */
  inside: boolean;
  x: number;
  y: number;
  octant: number;
}

/**
 * THE shared centre-band projection for off-frame cues. `Hud.edgeFlare` and the
 * chute indicator model both call this so the two channels can never drift
 * apart: same 46 px margin, same edge scale, same `[w*0.24, w*0.76] x
 * [h*0.32, h*0.68]` band clamp, same octant.
 */
export function edgeBandPlacement(
  point: { x: number; y: number },
  view: { width: number; height: number },
  margin = EDGE_BAND_MARGIN_PX,
): EdgeBandPlacement {
  const { width: w, height: h } = view;
  const cx = w / 2;
  const cy = h / 2;
  const inside = point.x >= margin && point.x <= w - margin && point.y >= margin && point.y <= h - margin;
  let dx = point.x - cx;
  let dy = point.y - cy;
  if (Math.abs(dx) < 1e-3 && Math.abs(dy) < 1e-3) dy = 1;
  const scale = Math.min(
    (w / 2 - margin) / Math.max(1e-3, Math.abs(dx)),
    (h / 2 - margin) / Math.max(1e-3, Math.abs(dy)),
  );
  const band = bandBounds(view);
  return {
    inside,
    x: clamp(cx + dx * scale, band.left, band.right),
    y: clamp(cy + dy * scale, band.top, band.bottom),
    octant: ((Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) % 8) + 8) % 8,
  };
}

export function bandBounds(view: { width: number; height: number }): {
  left: number; right: number; top: number; bottom: number;
} {
  return {
    left: view.width * 0.24,
    right: view.width * 0.76,
    top: view.height * 0.32,
    bottom: view.height * 0.68,
  };
}

/** 0 / 1-3 / 4-7 / 8+ bulk → the four escalation steps shared by pips and chevrons. */
export function chuteLevel(bulk: number): 0 | 1 | 2 | 3 {
  if (bulk <= 0) return 0;
  if (bulk <= 3) return 1;
  if (bulk <= 7) return 2;
  return 3;
}

/**
 * A band point already owned by ANOTHER off-screen channel this frame — i.e. a
 * live `Hud.edgeFlare` (`event-jam-alt`, `event-jam-<lane>`, `event-blast`).
 * Chevrons must give way to these: both channels clamp into the SAME band, and
 * `hudReadabilityIssues` flags overlap between every pair of `data-hud-element`s.
 */
export interface ReservedBandPoint {
  id: string;
  x: number;
  y: number;
}

/**
 * Build one spec per signal. `hidden` specs still carry their level and label —
 * the BINS meter pips read them in both modes, whether or not a chevron is up.
 *
 * `previous` is last frame's output; it only feeds the show/hide hysteresis, so
 * the function stays a pure function of its inputs.
 *
 * `reserved` is the OTHER channel's live band points (see ReservedBandPoint):
 * the separation pass treats them as immovable, and a chevron that still cannot
 * clear one is demoted to `hidden` — the urgent event cue outranks a backlog
 * count, and the backlog is still readable on the BINS pips, which read hidden
 * specs too.
 */
export function chuteIndicatorModel(
  signals: readonly ChuteSignal[],
  view: { width: number; height: number },
  opts: { simple: boolean; motionReduced: boolean },
  previous: readonly ChuteIndicatorSpec[] = [],
  reserved: readonly ReservedBandPoint[] = [],
): ChuteIndicatorSpec[] {
  // Zero-size viewport (pre-layout / detached container): no-op, exactly like
  // `Hud.edgeFlare`, which bails on a 0-width root rect.
  if (view.width <= 0 || view.height <= 0) return [];

  const wasEdge = new Set(previous.filter((spec) => spec.kind === "edge").map((spec) => spec.id));
  const specs: ChuteIndicatorSpec[] = signals.map((signal) => {
    const bulk = Math.max(0, signal.pileBulk) + Math.max(0, signal.incomingBulk);
    const level = chuteLevel(bulk);
    // Hysteresis: 46 px to show, 56 px to hide (an already-raised chevron must
    // see its chute come further inside the frame before it retracts).
    const margin = wasEdge.has(signal.id) ? EDGE_BAND_HIDE_MARGIN_PX : EDGE_BAND_MARGIN_PX;
    const placement = edgeBandPlacement({ x: signal.screenX, y: signal.screenY }, view, margin);
    // A chute that is on screen is already told by the in-world mouth flash.
    const kind: "edge" | "hidden" = level === 0 || placement.inside ? "hidden" : "edge";
    return {
      id: signal.id,
      kind,
      x: placement.x,
      y: placement.y,
      octant: placement.octant,
      level,
      bulk,
      label: opts.simple ? `${bulk}` : `${CHUTE_DIRECTION[signal.id]} · ${bulk}`,
      // Flash reduction downgrades a full chute to a STEADY chevron, never a
      // blinking one (12.3 / 18.5 flash policy).
      pulse: kind === "edge" && level === 3 && !opts.motionReduced,
    };
  });

  separate(specs.filter((spec) => spec.kind === "edge"), view, reserved);
  return specs;
}

/**
 * Deterministic anti-collision pass. `hudReadabilityIssues` flags overlap
 * between EVERY pair of `data-hud-element`s, so two chutes off-screen in the
 * same direction (the common F3 case — from mid-arena both flanks fall off the
 * same frame edge) must never land on top of each other.
 *
 * Specs are ordered by id, then relaxed apart along the band's VERTICAL axis:
 * the chevrons are wide, short pills, so stacking rows is the axis that
 * actually guarantees non-overlapping rects, and the band is 0.36 x height tall
 * — ample for three rows at 48 px. Horizontal position (and therefore the
 * direction the player reads) is preserved.
 *
 * CROSS-CHANNEL: `reserved` carries the event flares already occupying the same
 * band (`event-jam-alt` is up for the whole of an adult jam, and chevrons are up
 * almost constantly in adult play, so this collision is routine — not a corner
 * case). Reserved points are immovable; a chevron that cannot clear one inside
 * the band is demoted to `hidden` rather than stacked on top of it.
 */
function separate(
  specs: ChuteIndicatorSpec[],
  view: { width: number; height: number },
  reserved: readonly ReservedBandPoint[] = [],
): void {
  if (specs.length === 0) return;
  if (specs.length < 2 && reserved.length === 0) return;
  const band = bandBounds(view);
  const ordered = [...specs].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (let pass = 0; pass < SEPARATION_PASSES; pass++) {
    let adjusted = false;
    // Yield to the event channel first: those points never move, so a chevron
    // is pushed to whichever side of the flare it is already nearer.
    for (const spec of ordered) {
      for (const point of reserved) {
        if (!tooClose(spec, point)) continue;
        adjusted = true;
        const target = spec.y >= point.y ? point.y + MIN_SEPARATION_PX : point.y - MIN_SEPARATION_PX;
        spec.y = clamp(target, band.top, band.bottom);
      }
    }
    for (let i = 0; i < ordered.length; i++) {
      for (let j = i + 1; j < ordered.length; j++) {
        const a = ordered[i]!;
        const b = ordered[j]!;
        if (!tooClose(a, b)) continue;
        adjusted = true;
        const push = (MIN_SEPARATION_PX - Math.abs(a.y - b.y)) / 2;
        // Tie-break by id order so the same inputs always yield the same rows.
        const upper = a.y <= b.y ? a : b;
        const lower = upper === a ? b : a;
        upper.y = clamp(upper.y - push, band.top, band.bottom);
        lower.y = clamp(lower.y + push, band.top, band.bottom);
      }
    }
    if (!adjusted) break;
  }
  const unresolved = ordered.some((a, i) => ordered.slice(i + 1).some((b) => tooClose(a, b)));
  if (unresolved) {
    // Band too shallow for the relaxation to converge (tiny viewport): fall back
    // to rows a full MIN_SEPARATION_PX apart, centred on the band. Deliberately
    // NOT band-height/rows — on a container short enough to reach this path that
    // spacing drops under the pill height and the rects overlap anyway, which is
    // the one thing this pass exists to prevent. The rows may then sit outside
    // the (preferred) band; they stay inside the frame's safe inset.
    const rows = ordered.length;
    const centre = (band.top + band.bottom) / 2;
    const first = centre - ((rows - 1) * MIN_SEPARATION_PX) / 2;
    for (let index = 0; index < rows; index++) {
      ordered[index]!.y = clamp(
        first + index * MIN_SEPARATION_PX,
        EDGE_BAND_MARGIN_PX,
        Math.max(EDGE_BAND_MARGIN_PX, view.height - EDGE_BAND_MARGIN_PX),
      );
    }
  }
  // Last resort against the OTHER channel: an event flare never moves, so a
  // chevron still sharing its row gives way entirely. The backlog it carries is
  // still on the BINS pips (which read hidden specs), and the flare is the more
  // urgent cue by construction.
  for (const spec of ordered) {
    if (!reserved.some((point) => tooClose(spec, point))) continue;
    spec.kind = "hidden";
    spec.pulse = false;
  }
}

function tooClose(a: { x: number; y: number }, b: { x: number; y: number }): boolean {
  return Math.abs(a.x - b.x) < ROW_CLEARANCE_PX && Math.abs(a.y - b.y) < MIN_SEPARATION_PX;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
